import React, { useContext, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { LinkContainer } from 'react-router-bootstrap'
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBoxesStacked,
  faCashRegister,
  faChartLine,
  faPlus,
  faSackDollar,
  faTags,
  faTruck
} from '@fortawesome/free-solid-svg-icons'
import { getinventorySummary } from '../actions/inventoryActions'
import { getLowInventory } from '../actions/reportActions'
import Message from '../components/Message'
import Loader from '../components/Loader'
import { ThemeContext } from '../components/ThemeContext.js'
import {
  EMPLOYEE_PERMISSIONS,
  hasEmployeePermission
} from '../constants/employeePermissions.js'

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  })

const Dashboard = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { theme } = useContext(ThemeContext)

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const canManageInventory = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.INVENTORY_MANAGE
  )
  const canViewReports = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.REPORTS_VIEW
  )

  const inventorySummary = useSelector((state) => state.inventorySummary)
  const {
    loading,
    error,
    inventoryCount,
    lowInventoryCount,
    manufacturerCount,
    itemCount,
    inventorycost,
    inventoryvalue
  } = inventorySummary
  const lowInventoryState = useSelector((state) => state.lowInventory)
  const displayedLowInventoryCount =
    typeof lowInventoryCount === 'number' ? lowInventoryCount : (
      lowInventoryState.lowcount
    )

  useEffect(() => {
    if (!userInfo && !ownerInfo) {
      localStorage.removeItem('userInfo')
      navigate('/login')
    } else if (!canViewReports) {
      navigate('/pos')
    } else {
      dispatch(getinventorySummary())
    }
  }, [navigate, userInfo, ownerInfo, canViewReports, dispatch])

  const dashboardStyle = {
    color: theme.text,
    '--dashboard-border': theme.surfaceMuted,
    '--dashboard-card': theme.card,
    '--dashboard-text': theme.text,
    '--dashboard-muted': theme.subtext,
    '--dashboard-accent': theme.accent,
    '--dashboard-icon-bg': theme.surfaceMuted
  }

  const metrics = [
    {
      label: 'Inventory units',
      value: Number(inventoryCount ?? 0).toLocaleString('en-US'),
      detail: 'Units currently in stock',
      icon: faBoxesStacked
    },
    {
      label: 'Catalog items',
      value: Number(itemCount ?? 0).toLocaleString('en-US'),
      detail: 'Products in your catalog',
      icon: faTags
    },
    {
      label: 'Suppliers',
      value: Number(manufacturerCount ?? 0).toLocaleString('en-US'),
      detail: 'Manufacturers in your directory',
      icon: faTruck
    },
    {
      label: 'Retail value',
      value: formatCurrency(inventoryvalue),
      detail: `At cost: ${formatCurrency(inventorycost)}`,
      icon: faSackDollar
    }
  ]

  const actions = [
    {
      label: 'Open sales terminal',
      description: 'Start a sale or check out a customer',
      path: '/pos',
      icon: faCashRegister
    },
    {
      label: 'Manage inventory',
      description: 'Review current stock levels',
      path: '/inventory',
      icon: faBoxesStacked
    },
    ...(canManageInventory ?
      [
        {
          label: 'Add inventory',
          description: 'Record new stock',
          path: '/inventory/add',
          icon: faPlus
        }
      ]
    : []),
    {
      label: 'View reports',
      description: 'Explore sales and inventory reports',
      path: '/reports',
      icon: faChartLine
    }
  ]

  return (
    <Container className='dashboard-page' style={dashboardStyle}>
      <div className='dashboard-heading'>
        <div>
          <h1>Dashboard</h1>
          <p className='mb-0' style={{ color: theme.subtext }}>
            A clear view of your stock, catalog, and inventory value.
          </p>
        </div>
      </div>

      {loading ?
        <Loader />
      : error ?
        <Message variant='danger'>{error}</Message>
      : <>
          {inventoryCount === 0 && (
            <Alert variant='info' className='mb-4'>
              Your inventory is empty. Add stock to get started.
            </Alert>
          )}
          <Alert
            variant={
              typeof displayedLowInventoryCount !== 'number' ? 'secondary'
              : displayedLowInventoryCount > 0 ?
                'warning'
              : 'success'
            }
            className={`dashboard-stock-alert d-flex align-items-center justify-content-between flex-wrap gap-3 ${
              typeof displayedLowInventoryCount !== 'number' ?
                'dashboard-stock-alert-unavailable'
              : displayedLowInventoryCount > 0 ? 'dashboard-stock-alert-warning'
              : 'dashboard-stock-alert-success'
            }`}
            style={{
              '--dashboard-alert-text': theme.text,
              '--dashboard-alert-background': theme.card,
              '--dashboard-alert-border': theme.surfaceMuted
            }}
          >
            <div>
              {typeof displayedLowInventoryCount !== 'number' ?
                <>
                  <strong>Could not check low-stock status</strong>
                  <div className='small'>
                    {lowInventoryState.error ||
                      'The low-inventory report did not return a count.'}
                  </div>
                </>
              : displayedLowInventoryCount > 0 ?
                <>
                  <strong>
                    {displayedLowInventoryCount}{' '}
                    {displayedLowInventoryCount === 1 ?
                      'item needs'
                    : 'items need'}{' '}
                    attention
                  </strong>
                  <div className='small'>
                    Stock is at or below its low-inventory threshold.
                  </div>
                </>
              : <>
                  <strong>Stock levels look good</strong>
                  <div className='small'>
                    No items are at or below their low-inventory threshold.
                  </div>
                </>
              }
            </div>
            <LinkContainer to='/reports/lowinventory'>
              <Button
                variant='primary'
                size='sm'
                className='dashboard-alert-action'
              >
                {(
                  typeof displayedLowInventoryCount === 'number' &&
                  displayedLowInventoryCount > 0
                ) ?
                  'Review low inventory'
                : 'View low inventory'}
              </Button>
            </LinkContainer>
          </Alert>

          <section aria-labelledby='inventory-overview-heading'>
            <h2
              id='inventory-overview-heading'
              className='dashboard-section-title'
            >
              Inventory overview
            </h2>
            <Row className='g-3'>
              {metrics.map((metric) => (
                <Col key={metric.label} xs={12} sm={6} xl={3}>
                  <Card
                    className='dashboard-stat-card h-100'
                    style={{
                      backgroundColor: theme.card,
                      color: theme.text
                    }}
                  >
                    <Card.Body>
                      <div className='d-flex align-items-center justify-content-between gap-3'>
                        <div>
                          <div
                            className='dashboard-stat-label'
                            style={{ color: theme.subtext }}
                          >
                            {metric.label}
                          </div>
                          <div className='dashboard-stat-value'>
                            {metric.value}
                          </div>
                        </div>
                        <span
                          className='dashboard-stat-icon'
                          aria-hidden='true'
                        >
                          <FontAwesomeIcon icon={metric.icon} />
                        </span>
                      </div>
                      <div
                        className='small mt-2'
                        style={{ color: theme.subtext }}
                      >
                        {metric.detail}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>

          <section className='mt-4' aria-labelledby='quick-actions-heading'>
            <h2 id='quick-actions-heading' className='dashboard-section-title'>
              Quick actions
            </h2>
            <Row className='g-3'>
              {actions.map((action) => (
                <Col key={action.path} xs={12} sm={6} xl={3}>
                  <LinkContainer to={action.path}>
                    <Button
                      variant='light'
                      className='dashboard-action-card p-3 w-100'
                    >
                      <div className='d-flex align-items-start gap-3'>
                        <FontAwesomeIcon
                          icon={action.icon}
                          className='mt-1'
                          aria-hidden='true'
                        />
                        <span>
                          <strong className='d-block'>{action.label}</strong>
                          <span
                            className='small d-block mt-1'
                            style={{ color: theme.subtext }}
                          >
                            {action.description}
                          </span>
                        </span>
                      </div>
                    </Button>
                  </LinkContainer>
                </Col>
              ))}
            </Row>
          </section>
        </>
      }
    </Container>
  )
}

export default Dashboard
